// @ts-nocheck
import React, { useCallback, useMemo } from "react";
import { Container } from "react-bootstrap";

// ─── Skeleton pulse ───────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("acc-styles")) {
  const s = document.createElement("style");
  s.id = "acc-styles";
  s.textContent = `
    @keyframes _acc_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._acc_sk { animation: _acc_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
  `;
  document.head.appendChild(s);
}
import { useNavigate } from "react-router-dom";
import { toast } from "../utilities/toast";
import { useUserContext } from "../context/UserContext";
import { Seo } from "../components/Seo";
import { generateNoIndexSEO } from "../lib/seo/metaGenerators";
import {
  ChevronRight,
  Receipt,
  ShieldLock,
  Telephone,
  Whatsapp,
  EnvelopeFill,
  ChatDotsFill,
  BoxArrowRight,
  PersonFill,
  GiftFill,
} from "react-bootstrap-icons";
import { QUICK_LINKS, ROLE_TILES, claimFromClaims } from "../config/roleLinks";
import brandLogo from "/imgs/logo/brand-name-500.png";

// ─── Primitives ───────────────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <div style={{
      fontSize: "0.72rem",
      fontWeight: 600,
      color: "rgba(60,60,67,0.55)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "0 4px 6px",
    }}>
      {title}
    </div>
  );
}

function SettingsSection({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      {title && <SectionHeader title={title} />}
      <div style={{
        backgroundColor: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 13,
        overflow: "hidden",
      }}>
        {children}
      </div>
    </div>
  );
}

function SettingsRow({ Icon, iconBg, iconColor, label, desc, onClick, last = false }) {
  return (
    <div
      role="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 14px",
        cursor: "pointer",
        borderBottom: last ? "none" : "1px solid rgba(60,60,67,0.09)",
        WebkitTapHighlightColor: "transparent",
        transition: "background 0.15s ease",
      }}
      onPointerDown={(e) => e.currentTarget.style.background = "rgba(60,60,67,0.04)"}
      onPointerUp={(e)   => e.currentTarget.style.background = "transparent"}
      onPointerLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <Icon size={15} color={iconColor} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.9375rem", color: "#1c1c1e", fontWeight: 500, lineHeight: 1.3 }}>
          {label}
        </div>
        {desc && (
          <div style={{ fontSize: "0.8rem", color: "rgba(60,60,67,0.55)", marginTop: 1, lineHeight: 1.3 }}>
            {desc}
          </div>
        )}
      </div>

      <ChevronRight size={13} color="rgba(60,60,67,0.28)" style={{ flexShrink: 0 }} />
    </div>
  );
}

// ─── Skeleton components ──────────────────────────────────────────────────────

function Skel({ w, h, r = 8, style = {} }) {
  return (
    <div
      className="_acc_sk"
      style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }}
    />
  );
}

function SkeletonRow({ last = false }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "11px 14px",
      borderBottom: last ? "none" : "1px solid rgba(60,60,67,0.09)",
    }}>
      <Skel w={32} h={32} r={8} />
      <div style={{ flex: 1 }}>
        <Skel w="52%" h={13} r={5} style={{ marginBottom: 5 }} />
        <Skel w="36%" h={11} r={4} />
      </div>
      <Skel w={13} h={13} r={3} />
    </div>
  );
}

function SkeletonSection({ rows }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <Skel w={64} h={10} r={4} style={{ marginBottom: 8, marginLeft: 4 }} />
      <div style={{
        backgroundColor: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 13, overflow: "hidden",
      }}>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} last={i === rows - 1} />
        ))}
      </div>
    </div>
  );
}

function SkeletonPage() {
  return (
    <Container className="py-4" style={{ maxWidth: 600 }}>
      {/* Profile header card skeleton */}
      <div style={{
        backgroundColor: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 16,
        padding: "20px 18px 18px",
        marginBottom: 28,
      }}>
        <Skel w={160} h={44} r={5} style={{ marginBottom: 16 }} />
        <div style={{ height: 1, backgroundColor: "rgba(60,60,67,0.07)", marginBottom: 16 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Skel w={52} h={52} r="50%" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skel w="48%" h={14} r={5} style={{ marginBottom: 6 }} />
            <Skel w="34%" h={11} r={4} />
          </div>
        </div>
      </div>

      {/* Account + Privacy sections */}
      <SkeletonSection rows={2} />
      <SkeletonSection rows={1} />
      {/* Support */}
      <SkeletonSection rows={4} />
      {/* Legal */}
      <SkeletonSection rows={2} />

      {/* Action button */}
      <Skel w="100%" h={46} r={13} />
    </Container>
  );
}

// ─── Account ──────────────────────────────────────────────────────────────────

const Account: React.FC = () => {
  const { user, claims, isAuthenticated, isLoading, logout } = useUserContext();
  const navigate = useNavigate();

  const claim     = useMemo(() => claimFromClaims(claims), [claims]);
  const roleTile  = ROLE_TILES[claim];

  const rawName    = user?.displayName || user?.email?.split("@")[0] || "Account";
  const initials   = rawName.slice(0, 2).toUpperCase();
  const email      = user?.email ?? "";

  const handleLogout = useCallback(async () => {
    try {
      const loginPath = "/login";
      await logout();
      toast.success("Logged out");
      navigate(loginPath);
    } catch {
      toast.error("Failed to logout");
    }
  }, [logout, navigate]);

  if (isLoading) return <SkeletonPage />;

  return (
    <Container className="py-4" style={{ maxWidth: 600 }}>
      <Seo {...generateNoIndexSEO("Your Account")} />

      {/* ── Profile header card ──────────────────────────────── */}
      <div style={{
        backgroundColor: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 16,
        padding: "20px 18px 18px",
        marginBottom: 28,
      }}>
        {/* Brand logo */}
        <img
          src={brandLogo}
          alt="Teco Fast Delivery"
          style={{
            height: "clamp(40px, 10vw, 52px)",
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            opacity: 0.88,
            display: "block",
            marginBottom: 16,
          }}
        />

        {/* Hairline divider */}
        <div style={{ height: 1, backgroundColor: "rgba(60,60,67,0.07)", marginBottom: 16 }} />

        {/* User identity */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            backgroundColor: "rgba(0,122,255,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: "1.5px solid rgba(0,122,255,0.15)",
          }}>
            {user
              ? <span style={{ fontSize: "1.15rem", fontWeight: 700, color: "#007aff", letterSpacing: "-0.02em" }}>{initials}</span>
              : <PersonFill size={22} color="#007aff" />
            }
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em", lineHeight: 1.25 }}>
              {isAuthenticated ? rawName : "Guest"}
            </div>
            <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {email || "Not signed in"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Account ──────────────────────────────────────────── */}
      {isAuthenticated && (
        <SettingsSection title="Account">
          {claim === "customer" && (
            <SettingsRow
              Icon={Receipt}
              iconBg="rgba(0,122,255,0.1)"
              iconColor="#007aff"
              label="Orders"
              desc="View your order history"
              onClick={() => navigate("/account/orders")}
            />
          )}
          {claim === "customer" && (
            <SettingsRow
              Icon={GiftFill}
              iconBg="rgba(52,199,89,0.12)"
              iconColor="#34c759"
              label="Refer & Earn"
              desc="Invite friends, earn wallet credit"
              onClick={() => navigate("/account/referrals")}
              last={!roleTile}
            />
          )}
          {claim === "merchant" && (
            <SettingsRow
              Icon={Receipt}
              iconBg="rgba(0,122,255,0.1)"
              iconColor="#007aff"
              label="Orders"
              desc="View your order history"
              onClick={() => navigate("/store/orders")}
              last={!roleTile}
            />
          )}
          {roleTile && (
            <SettingsRow
              Icon={roleTile.Icon}
              iconBg="rgba(0,122,255,0.1)"
              iconColor="#007aff"
              label={roleTile.label}
              desc={roleTile.desc}
              onClick={() => navigate(roleTile.path(user))}
              last
            />
          )}
        </SettingsSection> 
    )}

      {/* ── Privacy & Security ───────────────────────────────── */}
      {isAuthenticated && (
        <SettingsSection title="Privacy & Security">
          <SettingsRow
            Icon={ShieldLock}
            iconBg="rgba(60,60,67,0.08)"
            iconColor="#3c3c43"
            label="Privacy & Security"
            desc="Reset password and manage access"
            onClick={() => navigate("/settings/security")}
            last
          />
        </SettingsSection>
      )}

      {/* ── Support ──────────────────────────────────────────── */}
      <SettingsSection title="Support">
        <SettingsRow
          Icon={Telephone}
          iconBg="rgba(52,199,89,0.12)"
          iconColor="#34c759"
          label="Call Us"
          desc="+254 700 000 000"
          onClick={() => window.open("tel:+254700000000")}
        />
        <SettingsRow
          Icon={Whatsapp}
          iconBg="rgba(37,211,102,0.1)"
          iconColor="#25d366"
          label="WhatsApp"
          desc="Chat with support"
          onClick={() => window.open("https://wa.me/254700000000", "_blank")}
        />
        <SettingsRow
          Icon={EnvelopeFill}
          iconBg="rgba(0,122,255,0.1)"
          iconColor="#007aff"
          label="Email Us"
          desc="support@fineats.com"
          onClick={() => window.open("mailto:support@fineats.com")}
        />
        <SettingsRow
          Icon={ChatDotsFill}
          iconBg="rgba(255,149,0,0.1)"
          iconColor="#ff9500"
          label="Feedback"
          desc="Share your experience"
          onClick={() => navigate("/feedback")}
          last
        />
      </SettingsSection>

      {/* ── Quick Links ──────────────────────────────────────── */}
      {/* Kept in sync with Footer.jsx, which only shows these on md+ screens */}
      <SettingsSection title="Quick Links">
        {QUICK_LINKS.map((link, i) => (
          <SettingsRow
            key={link.to}
            Icon={link.Icon}
            iconBg="rgba(60,60,67,0.07)"
            iconColor="#3c3c43"
            label={link.label}
            desc={link.desc}
            onClick={() => navigate(link.to)}
            last={i === QUICK_LINKS.length - 1}
          />
        ))}
      </SettingsSection>

      {/* ── Sign out / Sign in ────────────────────────────────── */}
      <div style={{ marginBottom: 48 }}>
        {isAuthenticated ? (
          <button
            onClick={handleLogout}
            style={{
              width: "100%",
              padding: "13px 16px",
              borderRadius: 13,
              border: "none",
              backgroundColor: "rgba(255,59,48,0.08)",
              color: "#ff3b30",
              fontSize: "0.9375rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <BoxArrowRight size={16} />
            Sign Out
          </button>
        ) : (
          <button
            onClick={() => navigate("/login")}
            style={{
              width: "100%",
              padding: "13px 16px",
              borderRadius: 13,
              border: "none",
              backgroundColor: "rgba(0,122,255,0.1)",
              color: "#007aff",
              fontSize: "0.9375rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign In
          </button>
        )}
      </div>

    </Container>
  );
};

export default Account;
